import { deepEqual } from '@nxtpeople/nxt-vue-library';
import { UseAxiosOptions, useAxios } from '@vueuse/integrations/useAxios';
import { AxiosRequestConfig } from 'axios';
import { MaybeRef } from 'vue';
import { RouteParams } from 'vue-router';
import router from '../router';

type UseFetchConfig<T> = AxiosRequestConfig<T> & UseAxiosOptions<T> & { params?: MaybeRef<any> };

export function useFetch<T>(url: MaybeRef<string>, config: UseFetchConfig<T> = {}) {
   const currentRouteName = useRoute().name;

   const lastUsedQuery = ref<RouteParams>();
   const lastUsedUrl = ref<string>();
   const isInitialFetch = ref(true);

   const localUrl = computed(() => (isRef(url) ? url.value : url));
   const localParams = computed(() => (isRef(config.params) ? config.params.value : config.params));

   const axiosContainer = useAxios<T>(localUrl.value, { ...config }, api, { ...config, immediate: false });

   const isLoadingWithoutSilentRefresh = computed(() => axiosContainer.isLoading && !axiosContainer.data.value);

   function localFetch([url, params]: [string, RouteParams]) {
      if (!isInitialFetch.value && router.currentRoute.value.name !== currentRouteName) return;
      isInitialFetch.value = false;

      const hasQueryChanged = lastUsedQuery.value && !deepEqual(lastUsedQuery.value, params);
      const hasUrlChanged = lastUsedUrl.value && !deepEqual(lastUsedUrl.value, url);
      if (hasQueryChanged || hasUrlChanged) axiosContainer.data.value = undefined;

      lastUsedQuery.value = params;
      lastUsedUrl.value = localUrl.value;

      axiosContainer.execute(url, { params });
   }

   watch([localUrl, localParams], localFetch, { deep: true, immediate: config.immediate !== false });

   return { ...axiosContainer, isLoadingWithoutSilentRefresh };
}

export function usePost<T>(url: string, config: AxiosRequestConfig<T> & UseAxiosOptions<T> = {}) {
   return useAxios<T>(url, { ...config, method: 'post' }, api, { immediate: false, ...config, abortPrevious: false });
}

export function usePut<T>(url: string, config: AxiosRequestConfig<T> & UseAxiosOptions<T> = {}) {
   return useAxios<T>(url, { ...config, method: 'put' }, api, { immediate: false, ...config, abortPrevious: false });
}

export function usePatch<T>(url: string, config: AxiosRequestConfig<T> & UseAxiosOptions<T> = {}) {
   return useAxios<T>(url, { ...config, method: 'patch' }, api, { immediate: false, ...config, abortPrevious: false });
}

export function useDelete<T>(url: string, config: AxiosRequestConfig<T> & UseAxiosOptions<T> = {}) {
   return useAxios<T>(url, { ...config, method: 'delete' }, api, { immediate: false, ...config, abortPrevious: false });
}
